import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { 
  AuditLogService, 
  AuditLogRequestDTO, 
  AuditLogResponseDTO, 
  PaginatedAuditLogResponse,
  AuditLogSummaryDTO 
} from '../../../services/audit-log.service';

@Component({
  selector: 'app-audit-logs',
  templateUrl: './audit-logs.component.html',
  styleUrls: ['./audit-logs.component.scss']
})
export class AuditLogsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // Estado de datos
  auditLogs: AuditLogResponseDTO[] = [];
  totalRecords = 0;
  loading = false;
  error: string | null = null;
  
  // Paginación
  currentPage = 1;
  pageSize = 25;
  totalPages = 0;

  // Filtros
  filters: AuditLogRequestDTO = {
    page: 1,
    pageSize: 25,
    orderBy: 'Timestamp',
    orderDirection: 'desc'
  };

  // Opciones de filtro
  httpMethods: string[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD'];
  statusCodeOptions = [
    { value: 200, label: '200 - OK' },
    { value: 201, label: '201 - Created' },
    { value: 204, label: '204 - No Content' },
    { value: 400, label: '400 - Bad Request' },
    { value: 401, label: '401 - Unauthorized' },
    { value: 403, label: '403 - Forbidden' },
    { value: 404, label: '404 - Not Found' },
    { value: 409, label: '409 - Conflict' },
    { value: 422, label: '422 - Unprocessable Entity' },
    { value: 500, label: '500 - Internal Server Error' }
  ];

  // Estado de la UI
  showFilters = true;
  showAdvancedFilters = false;
  selectedLog: AuditLogResponseDTO | null = null;
  showLogDetail = false;

  // Estadísticas
  summary: AuditLogSummaryDTO | null = null;
  showSummary = false;
  
  // Configuración de fechas
  maxDate = new Date();
  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  constructor(private auditLogService: AuditLogService) {
    // Configurar búsqueda con debounce
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.filters.endpoint = searchTerm || undefined;
      this.currentPage = 1;
      this.filters.page = 1;
      this.loadAuditLogs();
    });
  }

  ngOnInit(): void {
    this.loadAuditLogs();
    this.loadHttpMethods();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga la lista de audit logs
   */
  loadAuditLogs(): void {
    this.loading = true;
    this.error = null;
    
    this.filters.page = this.currentPage;
    this.filters.pageSize = this.pageSize;
    
    // Configurar fechas
    if (this.dateFrom) {
      this.filters.dateFrom = this.dateFrom;
    }
    if (this.dateTo) {
      const endOfDay = new Date(this.dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      this.filters.dateTo = endOfDay;
    }

    this.auditLogService.getAuditLogs(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PaginatedAuditLogResponse) => {
          this.auditLogs = response.data;
          this.totalRecords = response.totalRecords;
          this.totalPages = response.totalPages;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading audit logs:', error);
          this.error = 'Error al cargar los logs de auditoría';
          this.loading = false;
        }
      });
  }

  /**
   * Carga los métodos HTTP disponibles
   */
  loadHttpMethods(): void {
    this.auditLogService.getHttpMethods()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (methods) => {
          this.httpMethods = methods;
        },
        error: (error) => {
          console.error('Error loading HTTP methods:', error);
        }
      });
  }

  /**
   * Carga las estadísticas de auditoría
   */
  loadSummary(): void {
    this.auditLogService.getAuditSummary(this.dateFrom || undefined, this.dateTo || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => {
          this.summary = summary;
        },
        error: (error) => {
          console.error('Error loading audit summary:', error);
        }
      });
  }

  /**
   * Maneja la búsqueda por endpoint
   */
  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchSubject.next(target.value);
  }

  /**
   * Cambia la página actual
   */
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadAuditLogs();
    }
  }

  /**
   * Cambia el tamaño de página
   */
  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = parseInt(target.value);
    this.currentPage = 1;
    this.loadAuditLogs();
  }

  /**
   * Aplica los filtros
   */
  applyFilters(): void {
    this.currentPage = 1;
    this.filters.page = 1;
    this.loadAuditLogs();
  }

  /**
   * Limpia todos los filtros
   */
  clearFilters(): void {
    this.filters = {
      page: 1,
      pageSize: this.pageSize,
      orderBy: 'Timestamp',
      orderDirection: 'desc'
    };
    this.dateFrom = null;
    this.dateTo = null;
    this.currentPage = 1;
    this.loadAuditLogs();
  }

  /**
   * Cambia el ordenamiento
   */
  changeSort(column: string): void {
    if (this.filters.orderBy === column) {
      this.filters.orderDirection = this.filters.orderDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.filters.orderBy = column;
      this.filters.orderDirection = 'desc';
    }
    this.loadAuditLogs();
  }

  /**
   * Muestra el detalle de un log
   */
  showDetail(log: AuditLogResponseDTO): void {
    this.selectedLog = log;
    this.showLogDetail = true;
  }

  /**
   * Cierra el detalle del log
   */
  closeDetail(): void {
    this.showLogDetail = false;
    this.selectedLog = null;
  }

  /**
   * Alterna la visibilidad del resumen
   */
  toggleSummary(): void {
    this.showSummary = !this.showSummary;
    if (this.showSummary && !this.summary) {
      this.loadSummary();
    }
  }

  /**
   * Genera las páginas para la paginación
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    const start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  /**
   * Métodos de utilidad del servicio
   */
  formatBytes = this.auditLogService.formatBytes;
  formatDuration = this.auditLogService.formatDuration;
  getStatusCodeClass = this.auditLogService.getStatusCodeClass;
  getHttpMethodClass = this.auditLogService.getHttpMethodClass;
  truncateText = this.auditLogService.truncateText;
  formatDateTime = this.auditLogService.formatDateTime;

  /**
   * Verifica si es un log de error
   */
  isError(statusCode: number): boolean {
    return statusCode >= 400;
  }

  /**
   * Verifica si es una operación de modificación de datos
   */
  isDataModification(method: string): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
  }

  /**
   * Copia texto al portapapeles
   */
  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      // Aquí podrías mostrar un toast de confirmación
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  }

  /**
   * Descarga los logs filtrados como JSON
   */
  downloadLogs(): void {
    const dataStr = JSON.stringify(this.auditLogs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }

  /**
   * Establece filtros rápidos
   */
  setQuickFilter(type: string): void {
    this.clearFilters();
    
    switch (type) {
      case 'errors':
        this.filters.onlyErrors = true;
        break;
      case 'modifications':
        this.filters.onlyDataModifications = true;
        break;
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.dateFrom = today;
        this.dateTo = new Date();
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        this.dateFrom = weekAgo;
        this.dateTo = new Date();
        break;
    }
    
    this.applyFilters();
  }

  /**
   * TrackBy function para optimizar el rendering de la tabla
   */
  trackByLogId(index: number, log: AuditLogResponseDTO): number {
    return log.id;
  }

  /**
   * Referencias para usar en el template
   */
  Math = Math;
  JSON = JSON;
}
